import { useState, useEffect, useRef, useCallback } from 'react';

const IDLE_TIMEOUT = 10 * 60 * 1000;   // 10 minutes
const WARNING_DURATION = 30 * 1000;    // 30 seconds countdown
const THROTTLE_MS = 1000;
const STORAGE_KEY = 'admin_last_activity';
const LOGOUT_KEY = 'admin_session_logout';

interface InactivityTimerResult {
  showWarning: boolean;
  secondsLeft: number;
  stayLoggedIn: () => void;
  logoutNow: () => void;
}

export function useInactivityTimer(onLogout: () => void): InactivityTimerResult {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(30);

  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastThrottleRef = useRef(0);
  const isWarningRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  }, []);

  const triggerLogout = useCallback(() => {
    clearTimers();
    setShowWarning(false);
    isWarningRef.current = false;
    // Signal other tabs.
    try { localStorage.setItem(LOGOUT_KEY, String(Date.now())); } catch (_) {}
    onLogout();
  }, [clearTimers, onLogout]);

  const startCountdown = useCallback(() => {
    isWarningRef.current = true;
    setShowWarning(true);
    setSecondsLeft(30);

    let secs = 30;
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      secs -= 1;
      setSecondsLeft(secs);
      if (secs <= 0) {
        if (countdownRef.current) clearInterval(countdownRef.current);
        triggerLogout();
      }
    }, 1000);
  }, [triggerLogout]);

  const resetIdleTimer = useCallback(() => {
    if (isWarningRef.current) return; // don't reset once warning is shown
    clearTimers();
    idleTimerRef.current = setTimeout(startCountdown, IDLE_TIMEOUT);
  }, [clearTimers, startCountdown]);

  const stayLoggedIn = useCallback(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    isWarningRef.current = false;
    setShowWarning(false);
    setSecondsLeft(30);
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (_) {}
    resetIdleTimer();
  }, [resetIdleTimer]);

  // Activity handler (throttled).
  const handleActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastThrottleRef.current < THROTTLE_MS) return;
    lastThrottleRef.current = now;
    try { localStorage.setItem(STORAGE_KEY, String(now)); } catch (_) {}
    resetIdleTimer();
  }, [resetIdleTimer]);

  // Cross-tab sync via storage events.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        // Another tab had activity — reset our timer.
        if (!isWarningRef.current) resetIdleTimer();
      }
      if (e.key === LOGOUT_KEY && e.newValue) {
        // Another tab logged out — dismiss and logout here too.
        clearTimers();
        setShowWarning(false);
        isWarningRef.current = false;
        onLogout();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [resetIdleTimer, clearTimers, onLogout]);

  // Attach activity listeners.
  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(evt => window.addEventListener(evt, handleActivity, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(evt => window.removeEventListener(evt, handleActivity));
      clearTimers();
    };
  }, [handleActivity, resetIdleTimer, clearTimers]);

  return { showWarning, secondsLeft, stayLoggedIn, logoutNow: triggerLogout };
}
